import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkTable } from '@angular/cdk/table';
import {
  ChangeDetectionStrategy,
  AfterContentChecked,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { IconButtonColumn } from '../../columns';
import { TableColumn } from '../../models';
import { SortParams } from '../../sortable-table';

export interface TableConfig {
  banding?: boolean;
  draggable?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  standalone: false,
})
export class TableComponent<T> implements AfterContentChecked {
  @HostBinding('class') private get _classes() {
    return 'block overflow-auto scrollbar-thin';
  }

  @ViewChild(CdkTable) private table?: CdkTable<T>;

  @Output() rowClicked = new EventEmitter<T>();

  @Input() config?: TableConfig;

  @Input() data?: T[] | null = [];

  @Input() columns: TableColumn<unknown>[] = [];

  @Output() rowDragged = new EventEmitter<T[]>();

  @Output() sortChanged = new EventEmitter<SortParams>();

  draggableColumn = new IconButtonColumn({ fitContent: true }, () => ({
    type: 'clear',
    rounded: true,
    icon: 'menu-01',
  }));

  @Input() trackBy: TrackByFunction<T> = index => index;

  /** Optional per-row classes, added to every cell (cells paint the row background). */
  @Input() rowClasses?: (item: T) => string;

  private readonly dragThresholdPx = 5;
  private pointerDown: { x: number; y: number } | null = null;

  ngAfterContentChecked() {
    this.table?.updateStickyColumnStyles();
  }

  toggleSort(columnId: string) {
    this.columns.forEach(column => {
      if (column.id !== columnId) column.clearSort();
    });

    const target = this.columns.find(column => column.id === columnId);
    if (target) {
      target.toggleSort();
      if (target.config.sort && target.config.header) {
        this.sortChanged.emit({ sortBy: target.config.header, sortOrder: target.config.sort });
      } else {
        this.sortChanged.emit(null);
      }
    }
  }

  onRowMouseDown(event: MouseEvent) {
    // Only track primary-button presses; right/middle click must not leave stale coords.
    if (event.button !== 0) return;
    this.pointerDown = { x: event.clientX, y: event.clientY };
  }

  clearPointerDown() {
    this.pointerDown = null;
  }

  rowClick(rowData: T, event?: MouseEvent) {
    if (window.getSelection()?.toString()) {
      this.clearPointerDown();
      return;
    }

    if (this.pointerDown && event) {
      const dx = Math.abs(event.clientX - this.pointerDown.x);
      const dy = Math.abs(event.clientY - this.pointerDown.y);
      this.clearPointerDown();
      if (dx > this.dragThresholdPx || dy > this.dragThresholdPx) return;
    } else {
      this.clearPointerDown();
    }

    this.rowClicked.emit(rowData);
  }

  drop(event: CdkDragDrop<string[]>) {
    if (this.data) {
      moveItemInArray(this.data, event.previousIndex, event.currentIndex);
      this.refresh();

      this.rowDragged.emit(this.data);
    }
  }

  refresh() {
    this.table?.renderRows();
  }
}
